import React, {
    useCallback, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';

import { Loader, useModal } from '@jutro/components';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { ServiceManager } from '@jutro/legacy/services';
import { WniAccountBillingDetailsService as WniBillingService } from 'wnice-capability-billing';
import { WniPremiumReportService } from 'wnice-capability-premiumreport';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { LobIconUtil } from '@xengage/gw-portals-util-js';

import metadata from './BillingSummaryPage.metadata.json5';
import styles from './BillingSummaryPage.module.scss';
import messages from './BillingSummaryPage.messages';

import { Icon } from '@jutro/legacy/components';

const getTabHeader = (productCode, isOwed, label = '') => {
    const icon = LobIconUtil.getMaterialIcon(productCode);
    return (
        <div id={`divFor${productCode}${label}`} className={styles.tabContent}>
            {productCode ? (
                <Icon id={`iconFor${productCode}${label}`} icon={icon} title={label} />
            ) : null}
            {isOwed ? (
                <Icon className={styles.isOwedIcon} icon='gw-error-outline'/>
            ) : null}
            <span id={`labelFor${productCode}${label}`}>{label.toUpperCase()}</span>
        </div>
    );
};

function BillingSummaryPage(props) {
    const modalApi = useModal();
    const { match, authHeader, location, history } = props;
    const { params } = match;
    const { streamId } = params;

    const localeService = ServiceManager.getService('locale-service');
    const defaultCurrencyCode = localeService.getDefaultCurrencyCode().toUpperCase();

    const { PolicyService } = useDependencies('PolicyService');

    // screen states
    const [isLoadingData, updateIsLoadingData] = useState(true);
    /**
     * CEI-4617 skip confirm want to make a payment if user come from auto pay setting.
     */
    const [skipMakePaymentConfirm, setSkipMakePaymentConfirm] = useState(false);
    const [selectedInvoiceStreamId, updateSelectedInvoiceStreamId] = useState(streamId);
    const [selectedCurrency, updateSelectedCurrency] = useState(defaultCurrencyCode);
    const [activeTab, updateActiveTab] = useState(selectedInvoiceStreamId);
    const defaultScreen = 'policy-list'
    const [selectedScreen, updateSelectedScreen] = useState(_.get(location, 'state.toScreen') || defaultScreen);

    // fetched data
    const [accountBillingSummaryFromApi, setAccountBillingSummaryFromApi] = useState({});
    const [invoiceStreamDetailsMapFromApi, setInvoiceStreamDetailsMapFromApi] = useState(new Map());
    const [policySummariesFromApi, setPolicySummariesFromApi] = useState([]);
    const [payAsYouGoPoliciesFromApi, setPayAsYouGoPoliciesFromApi] = useState([]);

    const { accountLevelBilling = false, invoiceStreams = [] } = accountBillingSummaryFromApi;

    const updateMap = useCallback(
        (invoiceStreamId) => (fetchData) => {
            setInvoiceStreamDetailsMapFromApi(
                invoiceStreamDetailsMapFromApi.set(invoiceStreamId, fetchData)
            );

            const { totalValue } = fetchData;
            const { currency } = totalValue;
            updateSelectedCurrency(currency.toUpperCase());
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const sortAccountInvoiceStreams = (accountInvoiceStreams) => {
        const newAccountInvoiceStreams = _.cloneDeep(accountInvoiceStreams);
        const newInvoiceStreams = _.get(accountInvoiceStreams, 'invoiceStreams');
        const sortedInvoiceStreams = _.orderBy(newInvoiceStreams, ['label'], ['desc']);
        _.set(newAccountInvoiceStreams, 'invoiceStreams', sortedInvoiceStreams);
        return newAccountInvoiceStreams;
    }

    const fetchAccountLevelInvoiceStreams = useCallback(() => {
        let accountInvoicePromise;
        let policySummaryPromise;
        let payAsYouGoPoliciesPromise;

        if (_.isEmpty(accountBillingSummaryFromApi)) {
            accountInvoicePromise = WniBillingService.getAccountInvoiceStreamInformation(
                authHeader
            ).then((accountInvoiceStreams) => {
                const sortedAccountInvoiceStreams = sortAccountInvoiceStreams(accountInvoiceStreams);
                const {
                    accountLevelBilling: accountLevelBillingFromApi = false,
                    invoiceStreams: invoiceStreamsFromApi = []
                } = sortedAccountInvoiceStreams;
                // sort: newest to oldest
                // const sortInvoiceStreams = invoiceStreamsFromApi.sort((a, b) => {
                //     return new Date(b.createTime_Ext) - new Date(a.createTime_Ext);
                // })
                // _.set(accountInvoiceStreams, 'invoiceStreams', sortInvoiceStreams);

                let defaultInvoiceStreamId;
                if (invoiceStreamsFromApi.length > 0) {
                    defaultInvoiceStreamId = invoiceStreamsFromApi[0].internalId;
                }
                if (accountLevelBillingFromApi && invoiceStreamsFromApi.length === 1) {
                    defaultInvoiceStreamId = invoiceStreamsFromApi[0].internalId;
                    updateSelectedInvoiceStreamId(invoiceStreamsFromApi[0].internalId);
                    updateActiveTab(invoiceStreamsFromApi[0].internalId);
                }
                setAccountBillingSummaryFromApi(sortedAccountInvoiceStreams);
                return defaultInvoiceStreamId;
            });
        } else {
            accountInvoicePromise = Promise.resolve(accountBillingSummaryFromApi);
        }

        if (policySummariesFromApi.length === 0) {
            policySummaryPromise = PolicyService.getAccountPolicySummaries(authHeader).then(
                setPolicySummariesFromApi
            );
        } else {
            policySummaryPromise = Promise.resolve(policySummariesFromApi);
        }

        if (payAsYouGoPoliciesFromApi.length === 0) {
            payAsYouGoPoliciesPromise = WniPremiumReportService.getPayAsYouGoPolicies(authHeader).then(setPayAsYouGoPoliciesFromApi);
        } else {
            payAsYouGoPoliciesPromise = Promise.resolve(payAsYouGoPoliciesFromApi);
        }
        
        return Promise.all([accountInvoicePromise, policySummaryPromise, payAsYouGoPoliciesPromise]).catch(() => {
            modalApi.showAlert({
                title: messages.failedToLoadPolices,
                message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                status: 'error',
                icon: 'gw-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                history.push('/home');
            }, _.noop);
        });
    }, [
        PolicyService,
        history,
        authHeader,
        accountBillingSummaryFromApi,
        policySummariesFromApi,
        payAsYouGoPoliciesFromApi,
        modalApi
    ]);

    const getPaymentDetailsGroupFromMap = useCallback(
        (invoiceStreamId) => {
            return invoiceStreamDetailsMapFromApi.get(invoiceStreamId);
        },
        [invoiceStreamDetailsMapFromApi]
    );

    const fetchInvoiceDetailsByStreamId = useCallback(
        (invoiceStreamId, forceRefresh = false) => {
            const invoiceDetails = getPaymentDetailsGroupFromMap(invoiceStreamId);
            let invoiceDetailsPromise;

            if ((invoiceStreamId && _.isEmpty(invoiceDetails)) || forceRefresh) {
                invoiceDetailsPromise = WniBillingService.getInvoiceStreamDetails(
                    invoiceStreamId,
                    authHeader
                )
                    .then(updateMap(invoiceStreamId))
                    .catch(() => {
                        modalApi.showAlert({
                            title: messages.failedToLoadPolices,
                            message: messages.sorryWeCanNotLoadYourPolicyInformationAtThisTime,
                            status: 'error',
                            icon: 'gw-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).then(() => {
                            history.push('/home');
                        }, _.noop);
                    });
            } else {
                invoiceDetailsPromise = Promise.resolve(invoiceDetails);
            }

            return invoiceDetailsPromise;
        },
        [getPaymentDetailsGroupFromMap, authHeader, updateMap, history, modalApi]
    );

    const selectedLobCode = useMemo(() => {
        const foundInvoiceStreams = invoiceStreams.filter(
            (item) => item.internalId === selectedInvoiceStreamId
        );
        return foundInvoiceStreams.length > 0 ? foundInvoiceStreams[0].lobcode : undefined;
    }, [invoiceStreams, selectedInvoiceStreamId]);

    const onTabClick = useCallback(
        (invoiceStreamIdFromTabId) => {
            const ignoreDefaultScreen = _.get(location, 'state.ignoreDefaultScreen');
            if (!ignoreDefaultScreen) {
                updateSelectedScreen(defaultScreen);
            } else {
                _.set(location, 'state.ignoreDefaultScreen', undefined);
            }
            fetchInvoiceDetailsByStreamId(invoiceStreamIdFromTabId).finally(() => {
                updateActiveTab(invoiceStreamIdFromTabId);
                updateSelectedInvoiceStreamId(invoiceStreamIdFromTabId);
            });
            setSkipMakePaymentConfirm(false)
        },
        [fetchInvoiceDetailsByStreamId, location]
    );

    const reloadInvoiceDetails = useCallback(
        (invoiceStreamId, toScreen = defaultScreen, skipConfirmWantToPay) => {
            updateIsLoadingData(true);
            return fetchInvoiceDetailsByStreamId(invoiceStreamId, true).finally(() => {
                updateIsLoadingData(false);
                updateSelectedScreen(toScreen, skipConfirmWantToPay);
                if(skipConfirmWantToPay){
                    setSkipMakePaymentConfirm(true)
                }
            });
        },
        [fetchInvoiceDetailsByStreamId]
    );

    useEffect(() => {
        updateIsLoadingData(true);
        fetchAccountLevelInvoiceStreams().then((retValues) => {
            if (retValues) {
                const initStreamId = selectedInvoiceStreamId || retValues[0];
                fetchInvoiceDetailsByStreamId(initStreamId).finally(() => {
                    onTabClick(initStreamId);
                    updateIsLoadingData(false);
                });
            }
        });
        // execure once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const noData = useCallback(
        () => {
            return invoiceStreams.length === 0;
        },
        [invoiceStreams]
    );

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {
        noDataMessageContainer: {
            visible: noData(),
            message: messages.noBillingData
        },
        billingSummaryContainer: {
            visible: !noData(),
        },
        billingTabsId: {
            content: _.map(invoiceStreams, (item) => {
                return {
                    id: item.internalId,
                    type: 'element',
                    component: 'tab',
                    componentProps: {
                        heading: getTabHeader(item.lobcode, item.isOwed_Ext, item.label)
                    },
                    content: [
                        {
                            id: `billingContentFor${item.internalId}`,
                            type: 'container',
                            component: 'BillingSummary',
                            componentProps: {
                                accountLevelBilling,
                                invoiceStreamId: selectedInvoiceStreamId,
                                lobCode: selectedLobCode,
                                invoiceStreamDetails: getPaymentDetailsGroupFromMap(
                                    selectedInvoiceStreamId
                                ),
                                authHeader,
                                currency: selectedCurrency,
                                policySummaries: policySummariesFromApi,
                                payAsYouGoPolicies: payAsYouGoPoliciesFromApi,
                                reloadInvoiceDetails: reloadInvoiceDetails,
                                selectedScreen,
                                updateSelectedScreen,
                                skipMakePaymentConfirm
                            }
                        }
                    ]
                };
            }),
            activeTab: activeTab,
            onTabChange: onTabClick
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
}

BillingSummaryPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            streamId: PropTypes.string
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({}).isRequired
};

export default withAuthenticationContext(withRouter(BillingSummaryPage));
